import { ImageIcon } from '@primer/octicons-react';
import { Button } from '@primer/react';
import { compact } from 'lodash';
import { ReactNode, useState } from 'react';
import Modal from 'react-modal';
import styled from 'styled-components';
import { FeatureId } from '../../../../common/iam/access/feature-access/types';
import { ModelEntityWithScores } from '../../../../common/model/output/scores/model-entity-data/types';
import { RenderOptions } from '../../../../common/renderer/types';
import RenderView from '../../../views/render-view';
import { usePushGtmEvent } from '../components/google-tag-manager/use-push-gtm-event';
import { CustomGtmEvent } from '../components/google-tag-manager/use-push-gtm-event/payloads';
import { useHasAccessToFeature } from '../hooks';
import { NotAvailableModalContent } from '../modals/not-available-modal';
import { StandaloneModal } from '../modals/standalone-modal';
import { stylesOverride } from '../modals/styles-private';
import { NotAvailableCommonElements } from '../../upright/iam/access/feature-access/not-available-content/types';

interface Props {
  initialOptions: Partial<RenderOptions>;
  data?: Array<ModelEntityWithScores | undefined>;
  title?: ReactNode;
  button?: ReactNode;
  className?: string;
  trackEvent?: () => void;
  notAvailableCustomContent?: NotAvailableCommonElements;
}

export const RenderViewButton = (props: Props) => {
  const [modalOpen, setModalOpen] = useState(false);
  const data = props.data ? compact(props.data) : undefined;

  const hasAccess = useHasAccessToFeature(FeatureId.DOWNLOAD_IMAGE);
  const pushGtmEvent = usePushGtmEvent();

  const button = props.button ?? <DefaultButton />;

  return (
    <>
      <ButtonWrap
        className={props.className}
        onClick={() => {
          pushGtmEvent({
            event: CustomGtmEvent.IMPACT_PROFILE_IMAGE_DOWNLOAD_CLICK,
            data: {
              type: props.initialOptions.componentParams?.[0].componentType,
            },
          });
          props.trackEvent?.();
          setModalOpen(!modalOpen);
        }}
      >
        {button}
      </ButtonWrap>
      <StandaloneModal
        title={props.title !== undefined ? props.title : 'Download image'}
        isOpen={modalOpen}
        closeModal={() => {
          setModalOpen(false);
        }}
        modalName={'Download materiality matrix modal'}
        style={modalStyle}
      >
        {hasAccess ? (
          <RenderView initialOptions={props.initialOptions} data={data} />
        ) : (
          <NotAvailableModalContent
            featureId={FeatureId.DOWNLOAD_IMAGE}
            setModalOpen={setModalOpen}
            customContent={props.notAvailableCustomContent}
          />
        )}
      </StandaloneModal>
    </>
  );
};

const DefaultButton = () => {
  return (
    <Button variant="invisible" size={'small'} leadingVisual={ImageIcon}>
      Download image
    </Button>
  );
};

const ButtonWrap = styled.div`
  width: max-content;
`;

const modalStyle: Modal.Styles = {
  ...stylesOverride,
  content: {
    maxWidth: '1600px',
    minWidth: '40vw',
    minHeight: '40vh',
  },
};
